import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ViewKanban as ViewKanbanIcon,
  Speed as SpeedIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import apiService from '../services/api';
import { Project, Board } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface ProjectWithBoards {
  id: string;
  name: string;
  boards: Board[];
}

const Boards: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projectsWithBoards, setProjectsWithBoards] = useState<ProjectWithBoards[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState<ProjectWithBoards | null>(null);
  
  // Board creation state
  const [openCreateBoardModal, setOpenCreateBoardModal] = useState(false);
  const [boardForm, setBoardForm] = useState({
    name: '',
    type: 'kanban' as 'kanban' | 'scrum' | 'bau',
    projectId: ''
  });
  const [createBoardLoading, setCreateBoardLoading] = useState(false);
  const [allProjects, setAllProjects] = useState<Project[]>([]);

  useEffect(() => {
    loadAllProjectsBoards();
    loadAllProjects();
  }, []);

  const loadAllProjectsBoards = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get all projects the user has access to (use getUserProjects for regular users)
      const isAdmin = user?.role && ['admin', 'super_manager'].includes(user.role);
      const projects = isAdmin 
        ? await apiService.getProjects()
        : await apiService.getUserProjects();
      console.log('Loaded projects:', projects);
      
      // Load boards for each project
      const projectsWithBoardsData: ProjectWithBoards[] = [];
      
      for (const project of projects) {
        try {
          const projectBoards = await apiService.getBoardsForProject(project.id);
          projectsWithBoardsData.push({
            id: project.id,
            name: project.name,
            boards: projectBoards
          });
        } catch (err) {
          console.error(`Failed to load boards for project ${project.name}:`, err);
        }
      }
      
      setProjectsWithBoards(projectsWithBoardsData);
      console.log('Loaded projects with boards:', projectsWithBoardsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load projects and boards');
    } finally {
      setLoading(false);
    }
  };

  const loadAllProjects = async () => {
    try {
      const isAdmin = user?.role && ['admin', 'super_manager'].includes(user.role);
      const projects = isAdmin 
        ? await apiService.getProjects()
        : await apiService.getUserProjects();
      setAllProjects(projects || []);
    } catch (err) {
      console.warn('Failed to load projects for board creation', err);
    }
  };

  const handleCreateBoard = () => {
    setOpenCreateBoardModal(true);
  };

  const handleCloseCreateBoardModal = () => {
    setOpenCreateBoardModal(false);
    setBoardForm({
      name: '',
      type: 'kanban',
      projectId: ''
    });
  };

  const handleSaveBoard = async () => {
    if (!boardForm.name || !boardForm.projectId) return;

    try {
      setCreateBoardLoading(true);
      await apiService.createBoard({
        name: boardForm.name,
        type: boardForm.type,
        projectId: boardForm.projectId
      });
      await loadAllProjectsBoards();
      handleCloseCreateBoardModal();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create board');
    } finally {
      setCreateBoardLoading(false);
    }
  };

  const getBoardTypeIcon = (type: string) => {
    switch (type) {
      case 'kanban': return <ViewKanbanIcon />;
      case 'sprint': return <SpeedIcon />;
      case 'scrum': return <WorkIcon />;
      case 'bau': return <BusinessIcon />;
      default: return <AssignmentIcon />;
    }
  };

  const getBoardTypeColor = (type: string) => {
    switch (type) {
      case 'kanban': return 'primary';
      case 'sprint': return 'success';
      case 'scrum': return 'warning';
      case 'bau': return 'info';
      default: return 'default';
    }
  };

  // Filter projects based on selected project
  const getFilteredProjects = (projects: ProjectWithBoards[]) => {
    if (!selectedProject) {
      return projects;
    }
    return projects.filter(project => project.id === selectedProject.id);
  };

  // Filter boards based on search term
  const getFilteredBoards = (boards: Board[]) => {
    if (!searchTerm) {
      return boards;
    }
    return boards.filter(board => 
      board.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      board.type.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Get total filtered boards count
  const getTotalFilteredBoards = () => {
    const filteredProjects = getFilteredProjects(projectsWithBoards);
    return filteredProjects.reduce((total, project) => {
      const filteredBoards = getFilteredBoards(project.boards);
      return total + filteredBoards.length;
    }, 0);
  };

  if (loading) {
    return (
      <Layout maxWidth={false} disableGutters={false}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" sx={{ p: 3 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout maxWidth={false} disableGutters={false}>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout maxWidth={false} disableGutters={false}>
      <Box sx={{ p: 3, width: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1">
              Boards
            </Typography>
            <Typography variant="h6" sx={{ mt: 1, color: 'text.secondary' }}>
              {getTotalFilteredBoards()} boards across {getFilteredProjects(projectsWithBoards).length} projects
            </Typography>
          </Box>
          <Box sx={{ ml: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateBoard}
              disabled={allProjects.length === 0}
            >
              Create Board
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <TextField
                fullWidth
                placeholder="Search boards..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <Autocomplete
                options={projectsWithBoards}
                getOptionLabel={(option) => option.name}
                value={selectedProject}
                onChange={(event, newValue) => {
                  setSelectedProject(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select project..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.boards.length} boards
                      </Typography>
                    </Box>
                  </Box>
                )}
                clearOnEscape
                noOptionsText="No projects found"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setSelectedProject(null);
                }}
                fullWidth
              >
                Clear Filters
              </Button>
            </Grid>
            <Grid item xs={12} md={3} lg={6}>
              {/* Empty space to utilize full width */}
            </Grid>
          </Grid>
        </Paper>

        {/* All projects view */}
        {getFilteredProjects(projectsWithBoards).length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No projects match the current filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try adjusting your search criteria or clear the filters.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3} sx={{ width: '100%' }}>
            {getFilteredProjects(projectsWithBoards).map((project) => {
              const filteredBoards = getFilteredBoards(project.boards);
              return (
                <Grid item xs={12} key={project.id}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                      {project.name}
                    </Typography>
                    
                    {filteredBoards.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {project.boards.length === 0 
                          ? 'No boards in this project' 
                          : 'No boards match the current filters'
                        }
                      </Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {filteredBoards.map((board) => (
                          <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={board.id}>
                            <Card sx={{ height: '100%' }}>
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {board.name}
                                  </Typography>
                                  <Chip
                                    icon={getBoardTypeIcon(board.type)}
                                    label={board.type}
                                    color={getBoardTypeColor(board.type) as any}
                                    size="small"
                                  />
                                </Box>
                                
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {board.type === 'sprint' && 'Sprint-specific board for time-boxed development cycles'}
                                  {board.type === 'kanban' && 'General project board for ongoing work and backlog management'}
                                  {board.type === 'scrum' && 'Scrum framework board with sprints and ceremonies'}
                                  {board.type === 'bau' && 'Business as Usual board for ongoing operations and maintenance'}
                                </Typography>

                                {board.isDefault && (
                                  <Chip
                                    label="Default"
                                    color="secondary"
                                    size="small"
                                    sx={{ mb: 2 }}
                                  />
                                )}

                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => navigate(`/board/${project.id}?boardId=${board.id}`)}
                                  >
                                    View Board
                                  </Button>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* Create Board Modal */}
      <Dialog open={openCreateBoardModal} onClose={handleCloseCreateBoardModal} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Board</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select
                value={boardForm.projectId}
                label="Project"
                onChange={(e) => setBoardForm(prev => ({ ...prev, projectId: e.target.value }))}
              >
                <MenuItem value=""><em>Select a project</em></MenuItem>
                {allProjects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Board Name"
              value={boardForm.name}
              onChange={(e) => setBoardForm(prev => ({ ...prev, name: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Board Type</InputLabel>
              <Select
                value={boardForm.type}
                label="Board Type"
                onChange={(e) => setBoardForm(prev => ({ ...prev, type: e.target.value as any }))}
              >
                <MenuItem value="kanban">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ViewKanbanIcon />
                    Kanban - General workflow management
                  </Box>
                </MenuItem>
                {false && (
                  <MenuItem value="sprint">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SpeedIcon />
                      Sprint - Time-boxed development cycles
                    </Box>
                  </MenuItem>
                )}
                <MenuItem value="scrum">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WorkIcon />
                    Scrum - Agile development framework
                  </Box>
                </MenuItem>
                <MenuItem value="bau">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon />
                    BAU - Business as Usual operations
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {boardForm.type === 'kanban' && 'Kanban boards provide continuous workflow management with WIP limits and flow optimization.'}
              {boardForm.type === 'scrum' && 'Scrum boards follow the Scrum framework with sprints, ceremonies, and role-based workflows.'}
              {boardForm.type === 'bau' && 'BAU boards are optimized for ongoing business operations and maintenance tasks.'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateBoardModal}>Cancel</Button>
          <Button 
            onClick={handleSaveBoard} 
            variant="contained" 
            disabled={createBoardLoading || !boardForm.name || !boardForm.projectId}
          >
            {createBoardLoading ? 'Creating...' : 'Create Board'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Boards;